import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertTriangle, Phone } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 2500;
const MAX_ATTEMPTS = 12;

export default function MembershipSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return undefined;
    }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await axios.get(`${API}/membership/status/${sessionId}`);
        if (data.status === "active") return setStatus("active");
        if (data.status === "expired") return setStatus("error");
      } catch {
        // keep polling on transient errors
      }
      if (attempts.current >= MAX_ATTEMPTS) return setStatus("timeout");
      timer = setTimeout(poll, POLL_INTERVAL_MS);
      return undefined;
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <main data-testid="membership-success-page" className="pt-20 md:pt-24 min-h-screen flex items-center justify-center">
      <div className="max-w-xl mx-auto px-6 py-20 text-center">
        {status === "checking" && (
          <div data-testid="membership-success-checking">
            <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold text-white mb-2">Confirming Your Membership...</h1>
            <p className="text-zinc-400">Hold tight — we're verifying your payment with Stripe.</p>
          </div>
        )}
        {status === "active" && (
          <div data-testid="membership-success-active">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-6" />
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Welcome to Mason's Mobile Membership
            </h1>
            <p className="text-zinc-400 leading-relaxed mb-8">
              Your membership is active. Check your email for your receipt, membership
              information, and instructions for scheduling your first service.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/portal"
                data-testid="success-portal-button"
                className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-8 py-4 font-semibold transition-colors"
              >
                Go to My Member Portal
              </Link>
              <a
                href={PHONE_TEL}
                data-testid="success-call-button"
                className="inline-flex items-center justify-center gap-2 border border-zinc-700 hover:border-zinc-500 text-white rounded-sm px-8 py-4 font-semibold transition-colors"
              >
                <Phone className="h-4 w-4" /> Call {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        )}
        {(status === "error" || status === "timeout") && (
          <div data-testid="membership-success-error">
            <AlertTriangle className="h-14 w-14 text-amber-500 mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold text-white mb-3">
              {status === "timeout" ? "Still Processing" : "We Couldn't Confirm Your Payment"}
            </h1>
            <p className="text-zinc-400 leading-relaxed mb-8">
              {status === "timeout"
                ? "Your payment may still be processing. Check your Member Portal in a few minutes, or call us and we'll confirm right away."
                : "We couldn't process your payment. Please review your card and billing information or try another payment method."}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/membership#enroll" className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-8 py-4 font-semibold transition-colors">
                Try Again
              </Link>
              <a href={PHONE_TEL} className="inline-flex items-center justify-center gap-2 border border-zinc-700 text-white rounded-sm px-8 py-4 font-semibold">
                <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
